// Reddit Pixel event names this codebase fires — see RedditPixelScript for
// the base snippet. "Custom" is Reddit's escape hatch for anything outside
// its named standard events (see customEventName usage at call sites) —
// note it needs to be manually linked to a Conversion Event in Reddit's
// Events Manager before it's usable as an ad-optimization target, unlike
// the named standard events, which work immediately.
export type RedditPixelEvent = "Search" | "Lead" | "SignUp" | "ViewContent" | "Purchase" | "Custom";

const API_URL = process.env.NEXT_PUBLIC_API_URL || "";

export const sendRedditPixelEvent = (
  event: RedditPixelEvent,
  extraProps?: Record<string, unknown>
) => {
  // Shared between the pixel call below and the CAPI relay, so Reddit can
  // deduplicate the two copies of the same real action instead of double-
  // counting — confirmed required per Reddit's own CAPI setup docs ("If
  // you're using the Conversions API with the pixel, conversion_id is
  // required for deduplication").
  const conversionId =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  if (typeof window !== "undefined" && window.rdt) {
    try {
      window.rdt("track", event, { ...extraProps, conversion_id: conversionId });
    } catch {
      // Silently drop — must never affect the rest of the page
    }
  }

  sendRedditCapiRelay(event, conversionId, extraProps);
};

/**
 * Fire-and-forget POST to our own backend, which relays to Reddit's
 * Conversions API server-side — catches conversions the browser pixel
 * misses to ad blockers/ITP (see redditConversionsApi.js in the backend).
 * Never awaited by callers, same fire-and-forget posture as
 * itemTracking.ts's notifyCheckbot.
 *
 * value/currency/customEventName are pulled out of extraProps by
 * convention rather than requiring every call site to pass them
 * separately — every existing caller already uses exactly these key names
 * for this purpose (see itemTracking.ts, AddPriceMonitorModal,
 * usePriceIntelligence.ts, SaveInput, TrackProSubscription). Any other
 * extraProps keys (search_string, item_id, content_category, etc.) stay
 * pixel-only — the backend's schema doesn't have a slot for them and they
 * aren't needed for CAPI's own matching/dedup.
 *
 * Does NOT yet pass externalId/email match keys — none of this file's
 * callers have Clerk user data in scope at the call site (useUser() is a
 * hook, can't be called from this plain utility function), so CAPI-side
 * match quality is lower than the pixel's own page-load-time init call
 * until that's threaded through. A real future enhancement, not a
 * correctness bug — the backend already treats these fields as optional.
 */
function sendRedditCapiRelay(
  event: RedditPixelEvent,
  conversionId: string,
  extraProps?: Record<string, unknown>
) {
  if (typeof window === "undefined") return;

  const customEventName =
    typeof extraProps?.customEventName === "string" ? extraProps.customEventName : undefined;
  const value = typeof extraProps?.value === "number" ? extraProps.value : undefined;
  const currency = typeof extraProps?.currency === "string" ? extraProps.currency : undefined;

  try {
    fetch(`${API_URL}/api/v2/tracking/reddit/conversion`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        eventType: event,
        conversionId,
        ...(customEventName ? { customEventName } : {}),
        ...(value !== undefined || currency
          ? {
              metadata: {
                ...(value !== undefined ? { value } : {}),
                ...(currency ? { currency } : {}),
              },
            }
          : {}),
      }),
    }).catch(() => {
      // Silently drop — tracking must never surface to the user
    });
  } catch {
    // Silently drop
  }
}
